import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { AvvCustomService } from '../api/customServices/AvvCustomService';
import { addNotification } from '../store/slices/notificationSlice';
import {useAppSelector} from "../hooks/redux";

interface AvvDialogProps {
  onAccept?: () => void;
  onCancel?: () => void;
  show: boolean;
}

const AvvDialog: React.FC<AvvDialogProps> = ({ onAccept, onCancel, show }) => {
  const { t } = useTranslation();
  const {avv: {isAccepted}} = useAppSelector(state => state.profile);
  const dispatch = useDispatch();
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const isSelectingText = useRef(false);

  useEffect(() => {
    const fetchPdf = async () => {
      if (show) {
        try {
          const response: Blob = await AvvCustomService.downloadAvvPdf();
          // Convert Blob to base64 string
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            // Remove data URL prefix if present
            const base64Data = base64String.replace(/^data:application\/pdf;base64,/, '');
            setPdfData(base64Data);
          };
          reader.readAsDataURL(response);
        } catch (err) {
          dispatch(addNotification({
            type: 'error',
            title: 'Error',
            message: err instanceof Error ? err.message : t('avv_dialog_error_load'),
          }));
        }
      }
    };

    fetchPdf();
  }, [show, dispatch, t]);

  if (!show) return null;

  const handleAccept = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      // const response = await UserService.acceptAvv();
      // if (response instanceof Error) {
      //   dispatch(addNotification({
      //     type: 'error',
      //     title: 'Error',
      //     message: response.message || t('avv_dialog_error_accept'),
      //   }));
      //   return;
      // }
      
      // dispatch(addNotification({
      //   type: 'success',
      //   title: 'Success',
      //   message: t('avv_dialog_success'),
      // }));
      onAccept?.();
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: err instanceof Error ? err.message : t('avv_dialog_error_accept'),
      }));
    }
  };

  const handleMouseDown = () => {
    isSelectingText.current = true;
  };

  const handleMouseUp = () => {
    setTimeout(() => {
      isSelectingText.current = false;
    }, 10);
  };

  const handleCloseAttempt = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isSelectingText.current) {
      return;
    }
    
    event.stopPropagation();
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  return (
    <div>
      <div className="avv-dialog-overlay" onClick={handleCloseAttempt}>
        <div 
          ref={dialogRef}
          className={`avv-dialog ${isShaking ? 'shake' : ''}`} 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          <div className="avv-dialog__header">
            <h2 className="avv-dialog__header-title">
              {isAccepted ?
                  <>
                    {t('avv_accept_title')}
                  </>
                  :
                  <>
                    <span className="avv-dialog__header-color">{t('avv_dialog_title')}</span>: {t('avv_dialog_subtitle')}
                  </>
              }
            </h2>
          </div>
          
          <div className="avv-dialog__content">
            {pdfData ? (
              <iframe
                src={`data:application/pdf;base64,${pdfData}`}
                title={t('avv_dialog_contract_title')}
                className="avv-dialog__pdf"
              />
            ) : (
              <div className="avv-dialog__loading">{t('avv_dialog_loading')}</div>
            )}
          </div>

          <div className="avv-dialog__actions">
            {isAccepted ?
                <>
                  <button className="button button--primary" onClick={() => onCancel?.()}>Ok</button>
                </>
                :
                <>
                  <button
                      className="button button--primary"
                      onClick={handleAccept}
                      disabled={!pdfData}
                  >
                    {t('avv_dialog_accept')}
                  </button>
                </>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvvDialog; 